import { Fade, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';
import confetti from 'canvas-confetti';
import { range, shuffle } from 'lodash';
import React, { FC, useEffect, useState } from 'react';

const LoginDream = styled(Box)`
	&.login-dream {
		min-width: 390px;
		max-width: 430px;
		min-height: 87px;
		margin: 60px auto;
		border-radius: 56px;
		padding: 20px;
		background: #d7d7d7;
	}

	&.login-dream:before,
	&.login-dream:after {
		content: ' ';
		position: absolute;
		width: 0;
		height: 0;
	}

	&.login-dream:before {
		left: 30px;
		bottom: -50px;
	}

	&.login-dream:after {
		left: 40px;
		bottom: -29px;
		border: 15px solid;
		border-color: #d7d7d7 transparent transparent #d7d7d7;
	}
`;

interface ILoginPageBackgroundItem {
	right?: string;
	left?: string;
	top: number;
	emoji: string;
	text: string;
	textVariant: any;
	fadeIn?: boolean;
}

const LoginPageBackgroundItem: FC<ILoginPageBackgroundItem> = ({
	right,
	left,
	top,
	emoji,
	text,
	textVariant,
	fadeIn
}) => (
	<Fade
		in={fadeIn}
		timeout={Math.floor(Math.random() * (6000 - 4500 + 1) + 4500)}
	>
		<LoginDream
			position="absolute"
			right={right}
			left={left}
			top={top}
			className="login-dream"
		>
			<Box display="flex" flexDirection="row" alignItems="center">
				<Typography
					variant="h3"
					mr={2}
					onClick={() => {
						const randomInRange = (min: number, max: number) =>
							Math.random() * (max - min) + min;
						confetti({
							angle: randomInRange(55, 125),
							spread: randomInRange(50, 70),
							particleCount: randomInRange(50, 100),
							origin: { y: 0.6 }
						});
					}}
				>
					{emoji}
				</Typography>
				<Typography variant={textVariant}>{intl.tr(text)}</Typography>
			</Box>
		</LoginDream>
	</Fade>
);
LoginPageBackgroundItem.defaultProps = {
	left: 'none',
	right: 'none',
	fadeIn: false
};

const items = [
	{
		right: '10%',
		top: 50,
		text: 'loginPage.background.dream.1',
		emoji: '🙅',
		textVariant: 'h5'
	},
	{
		right: '6%',
		top: 300,
		text: 'loginPage.background.dream.2',
		emoji: '👩‍⚖️',
		textVariant: 'h6'
	},
	{
		right: '7%',
		top: 600,
		text: 'loginPage.background.dream.3',
		emoji: '💁‍♀️',
		textVariant: 'h6'
	},
	{
		left: '8%',
		top: 70,
		text: 'loginPage.background.dream.4',
		emoji: '🙋‍♀️',
		textVariant: 'h6'
	},
	{
		left: '6%',
		top: 300,
		text: 'loginPage.background.dream.5',
		emoji: '👩‍🎨',
		textVariant: 'h6'
	},
	{
		left: '8%',
		top: 550,
		text: 'loginPage.background.dream.6',
		emoji: '🧙',
		textVariant: 'h5'
	}
];

// random order of items is calculated only once
const itemsOrder = shuffle(range(0, items.length));

const LoginPageBackground: FC = () => {
	const [fadeIndex, setFadeIndex] = useState(-1);

	useEffect(() => {
		const interval = setInterval(
			() => setFadeIndex((prev) => (prev + 1) % items.length),
			4000
		);

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, []);

	return (
		<Box
			data-testid="login-page-background"
			id="login-page-background"
			display={{
				xs: 'none',
				sm: 'inherit'
			}}
		>
			{items.map((item, i) => (
				<LoginPageBackgroundItem
					{...item}
					key={item.text}
					fadeIn={itemsOrder[i] === fadeIndex}
				/>
			))}
		</Box>
	);
};

export default LoginPageBackground;
